import React, {useState} from 'react';
import Alert from '../../Alert';
import MarketCountryControls from '../MarketCountryControls';
import ShippingWizard from '../Shipping/ShippingWizard';
import {
    formatMarketCountries,
    normalizeMarketAvailabilityCountries,
    normalizeMarketCountries,
} from '../../../helpers/marketSettings';
import useDebouncedAutosave from './useDebouncedAutosave';

function getStorefrontShipToCountries(channel) {
    return normalizeMarketCountries(
        channel?.shipToCountries,
        [String(channel?.countryCode || 'US').toUpperCase()],
    );
}

function countriesMatch(left, right) {
    const normalizedLeft = normalizeMarketCountries(left, []);
    const normalizedRight = normalizeMarketCountries(right, []);
    return normalizedLeft.length === normalizedRight.length
        && normalizedLeft.every(country => normalizedRight.includes(country));
}

export default function ProductWizardShipping({
    product,
    channel,
    onNext,
    onBack,
    onSaveAndExit,
    saving,
    error,
    onAutosave,
}) {
    const inheritedShipToCountries = getStorefrontShipToCountries(channel);
    const productShipToCountries = normalizeMarketCountries(product?.shipToCountries);
    const productAvailableCountries = normalizeMarketAvailabilityCountries(
        product?.availableCountries,
    );
    const productRestrictedCountries = normalizeMarketCountries(product?.restrictedCountries);
    const shipToMatchesStorefront = countriesMatch(
        productShipToCountries,
        inheritedShipToCountries,
    );
    const [form, setForm] = useState({
        inheritShipTo: productShipToCountries == null || shipToMatchesStorefront,
        shipToCountries: productShipToCountries || inheritedShipToCountries,
        availableCountries: productAvailableCountries,
        restrictedCountries: productRestrictedCountries,
    });
    const usesStorefrontDefaults = form.inheritShipTo
        && !Array.isArray(form.availableCountries)
        && !Array.isArray(form.restrictedCountries);
    const effectiveShipToCountries = form.inheritShipTo
        ? inheritedShipToCountries
        : form.shipToCountries;
    const destinationSummary = usesStorefrontDefaults
        ? `Using storefront defaults · ${formatMarketCountries(inheritedShipToCountries)}`
        : 'Custom product overrides';

    function buildPayload() {
        return {
            shipToCountries: form.inheritShipTo ? null : form.shipToCountries,
            availableCountries: form.availableCountries,
            restrictedCountries: form.restrictedCountries,
        };
    }
    const {cancelAutosave} = useDebouncedAutosave(buildPayload(), onAutosave, {
        enabled: Boolean(product?.uuid),
    });

    const saveAndContinue = callback => {
        cancelAutosave();
        callback(buildPayload());
    };

    const destinationOverrides = (
        <>
            <details className="product-destination-overrides">
                <summary>
                    <span className="product-destination-overrides__summary">
                        <strong>Destination overrides</strong>
                        <span>{destinationSummary}</span>
                    </span>
                    <span
                        className="product-destination-overrides__edit"
                        aria-hidden="true"
                    />
                </summary>
                <div className="product-destination-overrides__body">
                    <p className="help">
                        Most products should use the storefront defaults. Only customize these
                        settings when this product ships to different countries or has legal,
                        licensing, or compliance restrictions.
                    </p>
                    <MarketCountryControls
                        idPrefix="product-shipping"
                        disabled={saving}
                        shipToCountries={form.shipToCountries}
                        onShipToCountriesChange={countries => setForm(current => ({
                            ...current,
                            shipToCountries: countries,
                        }))}
                        shipToHelp={
                            'Choose every country where you can deliver this physical product.'
                        }
                        inheritShipTo={form.inheritShipTo}
                        inheritedShipToCountries={inheritedShipToCountries}
                        onInheritShipToChange={inheritShipTo => (
                            setForm(current => ({...current, inheritShipTo}))
                        )}
                        showAvailability
                        availableCountries={form.availableCountries}
                        restrictedCountries={form.restrictedCountries}
                        onAvailabilityChange={availability => (
                            setForm(current => ({...current, ...availability}))
                        )}
                    />
                </div>
            </details>
            {error && <Alert type="error">{error}</Alert>}
        </>
    );

    return (
        <div className="wizard-step">
            <ShippingWizard
                productUuid={product.uuid}
                onComplete={() => saveAndContinue(onNext)}
                onBack={onBack}
                onSaveAndExit={() => saveAndContinue(onSaveAndExit)}
                embedded
                summaryTools={destinationOverrides}
                originCountry={String(channel?.countryCode || 'US').toUpperCase()}
                shipToCountries={effectiveShipToCountries}
            />
        </div>
    );
}
